import {
  isValidMobileSignupSource,
  mobileSignupSourceAtom,
} from '@/atoms/userCreationAtoms';
import { KINDE_EMAIL_CONNECTION_ID } from '@/components/constants/kinde';
import { type RootState } from '@/store';
import { isTauri } from '@/tauri/utils';
import { POLICY_EVALUATION } from '@/types/forceUpdatePolicy';
import { appendAmplitudeCrossDomainParams } from '@/utils/amplitudeCrossDomain';
import { appendAttributionParamsToRedirectUrl } from '@/utils/attributionParams';
import { isDetermineLoginAllowed } from '@/utils/auth/isDetermineLoginAllowed';
import { prefixWithLocale } from '@/utils/localeRedirect';
import { useTranslations } from '@/utils/translations';
import { AUTH0_LOGIN_PATH } from '@heidi/auth/contracts';
import { toAuth0UiLocales } from '@heidi/utils/i18n';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { z } from 'zod';

// Note: Error message from Zod is not displayed to users (only used for validation state)
// If error messages need to be shown, localize at the component level
const emailFormSchema = z.object({
  email: z.string().email(),
});

type EmailFormData = z.infer<typeof emailFormSchema>;

function isValidSsoEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

interface UseSignUpPageReturn {
  control: ReturnType<typeof useForm<EmailFormData>>['control'];
  email: string;
  isEmailInvalid: boolean;
  emailConnectionId: string;
  postLoginRedirectUri?: string;
  isLoading: boolean;
  isFromMobileWebview: boolean;
}

/**
 * Hook that manages state and logic for the new sign-up page.
 * Adapted from useSignInModalState to work with the full-page layout.
 */
export function useSignUpPage(): UseSignUpPageReturn {
  const router = useRouter();
  const { locale } = useTranslations();
  const setMobileSignupSource = useSetAtom(mobileSignupSourceAtom);

  // Helper function to add locale prefix if not already present
  const getRedirectUrl = (path: string) => prefixWithLocale(locale, path);

  const emailConnectionId = KINDE_EMAIL_CONNECTION_ID;

  // Detect mobile webview signup (iOS/Android) to bypass feature flags
  const isFromMobileWebview = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search);
    const source = params.get('source');
    const loginHint = params.get('loginHint');
    return (source === 'ios' || source === 'android') && !!loginHint;
  }, []);

  // Form state
  const {
    control,
    watch,
    reset,
    formState: { errors },
  } = useForm<EmailFormData>({
    resolver: zodResolver(emailFormSchema),
    defaultValues: { email: '' },
    mode: 'onBlur',
  });

  const email = watch('email');
  const isEmailInvalid = !!errors.email;

  // Check for force update (desktop app)
  const forceUpdateActive = useSelector((state: RootState) => {
    if (!isTauri()) return false;
    const result = state.forceUpdateModel?.evaluationResult;
    return result === POLICY_EVALUATION.FORCED;
  });

  // Post login redirect URI from props or query params
  const [postLoginRedirectUri, setPostLoginRedirectUri] = useState<string | undefined>();

  // Loading state for SSO redirect - initialize based on whether router is ready
  // to avoid unnecessary loading flash for non-SSO users
  const [isUsingSsoOrg, setIsUsingSsoOrg] = useState<boolean | null>(null);

  // Handle query params: post_login_redirect_url and SSO org redirect
  useEffect(() => {
    if (!router.isReady) return;

    // Set post login redirect from query
    const redirectUrl = router.query.post_login_redirect_url;
    if (typeof redirectUrl === 'string') {
      setPostLoginRedirectUri(
        appendAmplitudeCrossDomainParams(
          appendAttributionParamsToRedirectUrl(redirectUrl)
        )
      );
    }

    // Handle SSO org redirect
    const ssoOrg = router.query.sso_org;
    if (typeof ssoOrg !== 'string' || !ssoOrg) {
      setIsUsingSsoOrg(false);
      return;
    }

    const ssoEmail = `sso@${ssoOrg}`;
    if (!isValidSsoEmail(ssoEmail)) {
      setIsUsingSsoOrg(false);
      return;
    }

    setIsUsingSsoOrg(true);
    const ssoPostLoginRedirectUri = appendAmplitudeCrossDomainParams(
      appendAttributionParamsToRedirectUrl(
        getRedirectUrl(postLoginRedirectUri ?? '/scribe/new-user')
      )
    );

    if (isDetermineLoginAllowed()) {
      const params = new URLSearchParams();
      params.set('returnTo', ssoPostLoginRedirectUri);
      params.set('login_hint', ssoEmail);
      if (locale) params.set('ui_locales', toAuth0UiLocales(locale));
      void router.push(`${AUTH0_LOGIN_PATH}?${params.toString()}`);
    } else {
      void router.push({
        pathname: '/api/auth/login',
        query: {
          login_hint: ssoEmail,
          post_login_redirect_url: ssoPostLoginRedirectUri,
          connection_id: emailConnectionId,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getRedirectUrl depends on locale which is stable
  }, [
    router.isReady,
    router.query.post_login_redirect_url,
    router.query.sso_org,
    emailConnectionId,
    locale,
  ]);

  // Mobile webview signup: persist source param and pre-fill email from loginHint
  useEffect(() => {
    if (!router.isReady) return;

    const source = router.query.source;
    if (typeof source === 'string' && isValidMobileSignupSource(source)) {
      setMobileSignupSource(source);
    }

    const loginHint = router.query.loginHint;
    if (typeof loginHint === 'string' && loginHint) {
      reset({ email: loginHint });
    }
  }, [
    router.isReady,
    router.query.source,
    router.query.loginHint,
    setMobileSignupSource,
    reset,
  ]);

  // Show loading while router is not ready or SSO is being processed
  const isLoading = !router.isReady || forceUpdateActive || isUsingSsoOrg === true;

  return {
    control,
    email,
    isEmailInvalid,
    emailConnectionId,
    postLoginRedirectUri,
    isLoading,
    isFromMobileWebview,
  };
}
